import { styled } from '@linaria/react';
import { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { useDeletePage } from '../../../../api/pages-client/pages-client.hooks';
import { Page } from '../../../../api/pages-client/pages-client.type';
import { BrandedLoadingOverlay } from '../../../../components/loader/branded-loader';
import { DeleteConfirmationModal } from '../../../data-management/components/delete-confirmation-modal';
import { EmptyStateBox } from '../empty-state-box';
import { DashboardListItem } from './dashboards-list-item';

type Props = {
  pages: Page[];
  pagesQuery: UseQueryResult<Page[], Error>;
  handleCreateDashboard: () => void;
  handleEditDashboard: (dashboard: Page) => void;
};
export const DashboardsList = ({ pages, pagesQuery, handleCreateDashboard, handleEditDashboard }: Props) => {
  const [selectedDashboard, setSelectedDashboard] = useState<Page | null>(null);
  const [isDeleteDashboardOpen, setIsDeleteDashboardOpen] = useState(false);

  const deletePageMutation = useDeletePage();

  const handleDeleteDashboard = (dashboard: Page) => {
    setSelectedDashboard(dashboard);
    setIsDeleteDashboardOpen(true);
  };

  const onDeleteDashboard = () => {
    if (selectedDashboard?.id) {
      try {
        deletePageMutation.mutate(selectedDashboard.id);
      } catch (error) {
        console.error(error);
      }
    }
    setSelectedDashboard(null);
    setIsDeleteDashboardOpen(false);
  };

  if (!pagesQuery.isLoading && pages.length === 0) {
    return (
      <EmptyStateBox
        title="No Dashboards Found"
        icon="add_circle_outline"
        description="Create a dashboard to get started."
        onClick={handleCreateDashboard}
      />
    );
  }

  return (
    <>
      <BrandedLoadingOverlay visible={pagesQuery.isLoading} transitionDuration={30} variant="colored" />
      {
        <DashboardListGrid>
          {pages.map((page, i) => (
            <DashboardListItem key={i} page={page} onDelete={handleDeleteDashboard} onEdit={handleEditDashboard} />
          ))}
        </DashboardListGrid>
      }
      <DeleteConfirmationModal
        isOpen={isDeleteDashboardOpen}
        onClose={() => setIsDeleteDashboardOpen(false)}
        onDelete={onDeleteDashboard}
        title="Delete Dashboard"
        itemName={selectedDashboard ? selectedDashboard.name : ''}
        isDeleting={deletePageMutation.isPending}
        error={deletePageMutation.error}
      />
    </>
  );
};

export const DashboardListGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  margin: 0 auto;
  gap: 16px;
  cursor: default;
`;
